<template>
  <section class="trusted-partners-section mt-[13rem]">
    <h2 class="trusted-title text-[30px] lg:text-[5vw] xl:text-[80px] mx-5 lg:mx-32 text-left">
      A partner you can <span class="trusted-gradient">trust.</span>
    </h2>
    <div class="logos-grid lg:!hidden mt-4">
      <div v-for="logo in logos" :key="logo.alt" class="logo-item">
        <img :src="logo.src" :alt="logo.alt" />
      </div>
    </div>
    <div ref="desktopRowsBrands" class="hidden lg:flex lg:flex-col gap-y-4 overflow-hidden h-[100vh] mt-[2.563rem]">
      <img ref="topRow" src="../assets/logos/img.png" alt="firstRow" class="trusted-row" />
      <img ref="middleRow" src="../assets/logos/secondRow.png" alt="secondRow" class="trusted-row lg:mt-[5rem]" />
      <img ref="bottomRow" src="../assets/logos/img_3.png" alt="thirdRow" class="trusted-row lg:mt-[5rem]" />
    </div>
  </section>
</template>

<script setup lang="ts">
import katkin from '../assets/logos/katkin.png'
import ag1 from '../assets/logos/ag1.png'
import hb from '../assets/logos/hb.png'
import trip from '../assets/logos/trip.png'
import wild from '../assets/logos/img_1.png'
import spoke from '../assets/logos/spoke.png'
import oddballs from '../assets/logos/oddballs.png'
import polly from '../assets/logos/polly.png'
import phizz from '../assets/logos/phizz.png'
import heights from '../assets/logos/heights.png'
import frive from '../assets/logos/frive.png'
import numan from '../assets/logos/numan.png'
import spacegoods from '../assets/logos/spacegoods.png'
import buyer from '../assets/logos/buyer.png'
import freesoul from '../assets/logos/freesoul.png'
import zoe from '../assets/logos/zoe.png'
import runna from '../assets/logos/runna.png'
import yonder from '../assets/logos/yonder.png'
import carmoola from '../assets/logos/carmoola.png'
import purdyfigg from '../assets/logos/purdyfigg.png'
import monsoon from '../assets/logos/monsoon.png'
import champo from '../assets/logos/champo.png'
import skinMe from '../assets/logos/skinMe.png'
import monicaVinader from '../assets/logos/MV_LOGO_WHT.png'
import {ref, onMounted, nextTick, onUnmounted} from 'vue'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

const desktopRowsBrands = ref<HTMLElement | null>(null)
const topRow = ref<HTMLElement | null>(null)
const middleRow = ref<HTMLElement | null>(null)
const bottomRow = ref<HTMLElement | null>(null)
const logos = [
  { src: katkin, alt: 'Katkin' },
  { src: ag1, alt: 'AG1' },
  { src: hb, alt: 'H&B' },
  { src: trip, alt: 'TRIP' },
  { src: wild, alt: 'WILD' },
  { src: spoke, alt: 'SPOKE' },
  { src: oddballs, alt: 'Oddballs' },
  { src: polly, alt: 'polly' },
  { src: phizz, alt: 'PHIZZ' },
  { src: heights, alt: 'Heights' },
  { src: frive, alt: 'FRIVE' },
  { src: numan, alt: 'numan' },
  { src: spacegoods, alt: 'spacegoods' },
  { src: buyer, alt: 'buyer' },
  { src: freesoul, alt: 'Free Soul' },
  { src: zoe, alt: 'ZOE' },
  { src: runna, alt: 'runna' },
  { src: yonder, alt: 'yonder' },
  { src: carmoola, alt: 'carmoola' },
  { src: purdyfigg, alt: 'Purdy & Figg' },
  { src: monsoon, alt: 'MONSOON' },
  { src: champo, alt: 'CHAMPO' },
  { src: skinMe, alt: 'skin me' },
  { src: monicaVinader, alt: 'monicaVinader' },
]

let scrollTriggers: ScrollTrigger[] = []
let brandsObserver: IntersectionObserver | null = null

function createBrandsScrollTriggers() {
  if (!topRow.value || !middleRow.value || !bottomRow.value || !desktopRowsBrands.value) return;
  gsap.set(topRow.value, { xPercent: 40 });
  gsap.set(middleRow.value, { xPercent: -40 });
  gsap.set(bottomRow.value, { xPercent: 40 });

  scrollTriggers.push(ScrollTrigger.create({
    trigger: desktopRowsBrands.value,
    start: "top bottom",
    end: "bottom top",
    scrub: true,
    animation: gsap.to(topRow.value, { xPercent: 0, ease: "none" }),
  }));
  scrollTriggers.push(ScrollTrigger.create({
    trigger: desktopRowsBrands.value,
    start: "top bottom",
    end: "bottom top",
    scrub: true,
    animation: gsap.to(middleRow.value, { xPercent: 0, ease: "none" }),
  }));
  scrollTriggers.push(ScrollTrigger.create({
    trigger: desktopRowsBrands.value,
    start: "top bottom",
    end: "bottom top",
    scrub: true,
    animation: gsap.to(bottomRow.value, { xPercent: 0, ease: "none" }),
  }));
}

function killBrandsScrollTriggers() {
  scrollTriggers.forEach(st => st && st.kill());
  scrollTriggers = [];
  // Optionally reset rows to initial state
  if (topRow.value) gsap.set(topRow.value, { xPercent: 40 });
  if (middleRow.value) gsap.set(middleRow.value, { xPercent: -40 });
  if (bottomRow.value) gsap.set(bottomRow.value, { xPercent: 40 });
}

onMounted(() => {
  if (window.innerWidth >= 1024) {
    nextTick(() => {
      brandsObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            createBrandsScrollTriggers();
            window.addEventListener('resize', () => ScrollTrigger.refresh());
          } else {
            killBrandsScrollTriggers();
            window.removeEventListener('resize', () => ScrollTrigger.refresh());
          }
        });
      }, { threshold: 0.1 });
      if (desktopRowsBrands.value) brandsObserver.observe(desktopRowsBrands.value);
    });
  }

  // Mobile: animate logos one by one when grid is in view
  if (window.innerWidth < 1024) {
    const logoItems = document.querySelectorAll('.logos-grid .logo-item');
    gsap.set(logoItems, { opacity: 0, y: 40 });

    const mobObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          gsap.to(logoItems, {
            opacity: 1,
            y: 0,
            stagger: 0.2,
            duration: 0.6,
            ease: "power2.out"
          });
          observer.disconnect();
        }
      });
    }, { threshold: 0.2 });

    const grid = document.querySelector('.logos-grid');
    if (grid) mobObserver.observe(grid);
  }

  // Gradient text animation (unchanged)
  gsap.to(".trusted-gradient", {
    backgroundPosition: "300% center",
    duration: 4,
    repeat: -1,
    ease: "none"
  });
});

onUnmounted(() => {
  killBrandsScrollTriggers();
  if (brandsObserver) brandsObserver.disconnect();
  // Clean up ScrollTriggers
  scrollTriggers.forEach(st => st && st.kill());
  scrollTriggers = [];
  window.removeEventListener('resize', () => ScrollTrigger.refresh());
  ScrollTrigger.killAll();
});
</script>

<style scoped>
/* .trusted-row {
  opacity: 0;
  will-change: transform, opacity;
} */
.trusted-partners-section {
  background: #161816;
  padding: 3rem 0 2rem 0;
  text-align: center;
}
.trusted-title {
  color: #fff;
  font-weight: 500;
}
.trusted-gradient {
  background: linear-gradient(
      90deg,
      #52e475 0%,
      #3cdff8 25%,
      #e248ce 50%,
      #3cdff8 75%,
      #52e475 100%
  );
  background-size: 300% auto;
  background-position: 0% center;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  display: inline-block;
}
.logos-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2.5rem 2rem;
  justify-items: center;
  align-items: center;
}
.logo-item img {
  height: 62px;
  width: 62px;
  object-fit: contain;
}

/* Tablet */
@media (max-width: 1024px) {
  .logos-grid {
    grid-template-columns: repeat(4, 1fr);
    gap: 2rem 1.5rem;
  }
}

/* Mobile */
@media (max-width: 600px) {
  .logos-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 1.5rem 1rem;
  }
}
</style>

